/**
 * SRX-232 — Recharge Affinity portal mitigation.
 * Removes the "Inactive" subscriptions tab and its list panel so customers
 * cannot browse/reactivate cancelled (incl. MDi-unapproved) subscriptions.
 *
 * Cosmetic only: does not block Recharge API / direct reactivation.
 */

const PORTAL_PATH = '/tools/recurring';
const HIDDEN_ATTR = 'data-srx-inactive-hidden';

/** EN + ES labels Affinity uses on the status tabs (optional count suffix). */
const INACTIVE_TAB_LABEL =
  /^(inactive|inactivo|inactivos|inactivas)(\s*[·•]?\s*\(?\d+\)?)?$/i;

const isRechargePortal = () => window.location.pathname.includes(PORTAL_PATH);

const normalizeLabel = (value) =>
  (value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const getTabLabel = (el) =>
  normalizeLabel(el.getAttribute('aria-label') || el.textContent || '');

const hasInactiveTestId = (el) => {
  const testId = el.getAttribute('data-testid') || '';
  return /inactive/i.test(testId) && /(tab|panel|list|subscription)/i.test(testId);
};

const isInactiveTab = (el) => {
  if (hasInactiveTestId(el) && /tab/i.test(el.getAttribute('data-testid') || '')) {
    return true;
  }
  return INACTIVE_TAB_LABEL.test(getTabLabel(el));
};

const findRelatedPanel = (tab) => {
  const controlsId = tab.getAttribute('aria-controls');
  if (controlsId) {
    const byId = document.getElementById(controlsId);
    if (byId) return byId;
  }

  const tablist = tab.closest('[role="tablist"]');
  if (!tablist) return null;

  const tabs = [...tablist.querySelectorAll('[role="tab"]')];
  const index = tabs.indexOf(tab);
  if (index < 0) return null;

  const scope = tablist.parentElement || document;
  const panels = [...scope.querySelectorAll('[role="tabpanel"]')];
  return panels[index] || null;
};

const markAndRemove = (el) => {
  if (!el || el.hasAttribute(HIDDEN_ATTR)) return;
  el.setAttribute(HIDDEN_ATTR, '');
  el.remove();
};

const ensureActiveTabSelected = (tablist) => {
  if (!tablist) return;
  const tabs = [...tablist.querySelectorAll('[role="tab"]')];
  if (!tabs.length) return;

  const selected = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
  if (selected) return;

  const activeLike = tabs.find((tab) =>
    /^(active|activo|activos|activas)/i.test(getTabLabel(tab))
  );
  (activeLike || tabs[0]).click();
};

const scrubInactiveUi = (root = document) => {
  const tabCandidates = root.querySelectorAll(
    '[role="tab"], .recharge-tab, [data-testid*="tab" i]'
  );

  tabCandidates.forEach((tab) => {
    if (!isInactiveTab(tab)) return;

    const tablist = tab.closest('[role="tablist"]');
    const panel = findRelatedPanel(tab);

    markAndRemove(panel);
    markAndRemove(tab);
    ensureActiveTabSelected(tablist);
  });

  // Orphan inactive list/panel nodes Affinity may remount without aria wiring
  root.querySelectorAll('[data-testid*="inactive" i]').forEach((el) => {
    if (!hasInactiveTestId(el)) return;
    markAndRemove(el);
  });
};

const initHideRechargeInactiveTab = () => {
  if (!isRechargePortal()) return;

  scrubInactiveUi();

  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scrubInactiveUi();
    });
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
};

export default initHideRechargeInactiveTab;
